import type { ReactNode } from "react";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  EhDataTableShell,
  SortableHeader,
  TableSortResetBar,
  sortRowsByKey,
  type SortComparable,
  useTableSort,
} from "@/components/dataTable";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import {
  ZEITERFASSUNG_ACTION_COL_WIDTH,
  clampZeiterfassungColumnWidth,
} from "@/pages/admin/zeiterfassung/columns";

export interface ZeiterfassungTableColumn<T> {
  key: string;
  label: string;
  defaultWidth: number;
  render: (row: T) => ReactNode;
  sortValue?: (row: T) => SortComparable;
  className?: string;
  mono?: boolean;
  align?: "left" | "right" | "center";
}

export interface ZeiterfassungDataTableProps<T> {
  rows: T[];
  columns: ZeiterfassungTableColumn<T>[];
  rowKey: (row: T) => string | number;
  widths: Record<string, number>;
  onColumnWidthChange?: (key: string, width: number) => void;
  loading?: boolean;
  emptyText?: string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  actionColumn?: {
    label: string;
    headerIcon?: ReactNode;
    render: (row: T) => ReactNode;
  };
  footer?: ReactNode;
  renderMobileCard?: (row: T) => ReactNode;
  listFooter?: ReactNode;
  /** Tageserfassung: Tabelle auch auf kleinen Screens (keine Karten). */
  forceTableOnMobile?: boolean;
}

function getColumnWidth(
  key: string,
  defaultWidth: number,
  widths: Record<string, number>,
): number {
  const stored = Number(widths[key]);
  return Number.isFinite(stored) && stored > 0
    ? clampZeiterfassungColumnWidth(stored)
    : defaultWidth;
}

interface TheadProps {
  dataColumns: Array<{ key: string; label: string }>;
  actionColumn?: { label: string; headerIcon?: ReactNode };
  sort: { key: string; direction: "asc" | "desc" } | null;
  onSort: (key: string) => void;
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
}

function DataTableThead({
  dataColumns,
  actionColumn,
  sort,
  onSort,
  activeResize,
  onResizePointerDown,
}: TheadProps) {
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {dataColumns.map((col, idx) => (
          <th
            key={col.key}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3 py-2 text-left font-semibold leading-snug"
          >
            <SortableHeader
              label={col.label}
              columnKey={col.key}
              sort={sort}
              onSort={onSort}
            />
            <span
              className={clsx("eh-data-resizer", activeResize === col.key && "is-active")}
              role="separator"
              aria-orientation="vertical"
              title="Spaltenbreite ziehen"
              onPointerDown={(e) => onResizePointerDown(e, col.key, idx)}
            />
          </th>
        ))}
        {actionColumn && (
          <th
            scope="col"
            className="eh-data-action-cell whitespace-nowrap py-2 text-center text-[10px] font-semibold uppercase tracking-wide"
            style={{ right: 0 }}
            title={actionColumn.label}
          >
            <span className="sr-only">{actionColumn.label}</span>
            {actionColumn.headerIcon ?? (
              <span className="text-[1rem] leading-none" aria-hidden>
                ⋯
              </span>
            )}
          </th>
        )}
      </tr>
    </thead>
  );
}

function DesktopTable<T>({
  rows,
  columns,
  rowKey,
  widths,
  onColumnWidthChange,
  loading,
  emptyText = "Keine Einträge.",
  onRowClick,
  rowClassName,
  actionColumn,
  footer,
}: ZeiterfassungDataTableProps<T>) {
  const dataWidths = useMemo(
    () => columns.map((col) => getColumnWidth(col.key, col.defaultWidth, widths)),
    [columns, widths],
  );
  const { sort, toggle, reset } = useTableSort();
  const sortedRows = useMemo(
    () =>
      sortRowsByKey(rows, sort, (row, key) => {
        const column = columns.find((item) => item.key === key);
        return column?.sortValue?.(row) ?? String((row as Record<string, unknown>)[key] ?? "");
      }),
    [columns, rows, sort],
  );

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const colSpan = columns.length + (actionColumn ? 1 : 0);

  const floatingSyncKey = useMemo(
    () => [columns.map((c) => c.key).join("|"), dataWidths.join(","), rows.length].join("§"),
    [columns, dataWidths, rows.length],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: columns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? columns[idx]?.defaultWidth ?? 120;
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampZeiterfassungColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) onColumnWidthChange?.(colKey, lastWidth);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  return (
    <>
      <TableSortResetBar visible={sort != null} onReset={reset} />
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {columns.map((col, idx) => (
              <col
                key={col.key}
                ref={(el) => {
                  colRefs.current[col.key] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
            {actionColumn && (
              <col style={{ width: `${ZEITERFASSUNG_ACTION_COL_WIDTH}px` }} />
            )}
          </colgroup>

          <DataTableThead
            dataColumns={columns}
            actionColumn={actionColumn}
            sort={sort}
            onSort={toggle}
            activeResize={activeResize}
            onResizePointerDown={startResize}
          />

          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={colSpan}
                  className="px-3 py-8 text-center text-sm text-label-tertiary"
                >
                  {emptyText}
                </td>
              </tr>
            )}
            {sortedRows.map((row) => (
              <tr
                key={rowKey(row)}
                className={clsx(
                  "eh-data-row align-middle transition",
                  onRowClick && "cursor-pointer",
                  rowClassName?.(row),
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={clsx(
                      "eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-2 text-label",
                      col.mono && "font-mono tabular-nums",
                      col.align === "right" && "text-right",
                      col.align === "center" && "text-center",
                      col.className,
                    )}
                  >
                    {col.render(row)}
                  </td>
                ))}
                {actionColumn && (
                  <td
                    className="eh-data-action-cell align-middle py-2 text-center leading-none"
                    style={{ right: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {actionColumn.render(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {footer && <tfoot>{footer}</tfoot>}
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{ top: floating.top, left: floating.left, width: floating.width }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {columns.map((col, idx) => (
                    <col
                      key={col.key}
                      ref={(el) => {
                        floatingColRefs.current[col.key] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                  {actionColumn && (
                    <col style={{ width: `${ZEITERFASSUNG_ACTION_COL_WIDTH}px` }} />
                  )}
                </colgroup>
                <DataTableThead
                  dataColumns={columns}
                  actionColumn={actionColumn}
                  sort={sort}
                  onSort={toggle}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function MobileCards<T>({
  rows,
  loading,
  emptyText,
  renderMobileCard,
}: {
  rows: T[];
  loading?: boolean;
  emptyText: string;
  renderMobileCard: (row: T) => ReactNode;
}) {
  if (rows.length === 0 && !loading) {
    return (
      <div className="rounded-ios border border-dashed border-separator bg-card p-6 text-center text-sm text-label-tertiary">
        {emptyText}
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((row, idx) => (
        <li key={idx}>{renderMobileCard(row)}</li>
      ))}
    </ul>
  );
}

export function ZeiterfassungDataTable<T>(props: ZeiterfassungDataTableProps<T>) {
  const {
    rows,
    loading,
    emptyText = "Keine Einträge.",
    listFooter,
    renderMobileCard,
    forceTableOnMobile,
  } = props;

  return (
    <div>
      <div className={forceTableOnMobile ? "block" : "hidden md:block"}>
        <DesktopTable {...props} emptyText={emptyText} />
      </div>
      {!forceTableOnMobile && renderMobileCard && (
        <div className="md:hidden">
          <MobileCards
            rows={rows}
            loading={loading}
            emptyText={emptyText}
            renderMobileCard={renderMobileCard}
          />
        </div>
      )}
      {listFooter}
      {!loading && rows.length > 0 && !listFooter && !props.footer && (
        <div className="py-4 text-center text-xs text-label-tertiary">
          Ende der Liste · {rows.length} Einträge
        </div>
      )}
    </div>
  );
}
